import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { getShootingPreferences } from '@/services/data/shootingPreferences';
import { getUserPlanningPreferences } from '@/services/data/planningPreferences';
import { getAllPlatforms, initializeUserPlatforms, fetchMetricoolConnections, getConnectionLink, type MetricoolConnectionStatus } from '@/services/data/socialConnections';
import { getUserById } from '@/services/data/users';

/**
 * Hook to preload all onboarding data in parallel when the onboarding flow starts.
 * This eliminates loading spinners on each step transition.
 * Also prefetches the Metricool connection link for instant navigation.
 */
export function usePreloadOnboardingData() {
  const userId = useAuthStore((state) => state.userId);
  
  // Only subscribe to the reactive state we need to return
  const isLoading = useOnboardingDataStore((state) => state.isLoading);
  const isLoaded = useOnboardingDataStore((state) => state.isLoaded);
  const connectionLinkState = useOnboardingDataStore((state) => state.connectionLinkState);
  
  // Use ref to prevent multiple concurrent calls (but allow retries across mounts)
  const loadingRef = useRef(false);

  useEffect(() => {
    // Get store actions via getState() to avoid extra subscriptions
    const store = useOnboardingDataStore.getState();
    
    if (!userId) {
      store.reset();
      return;
    }

    // If already loaded or currently loading, don't reload
    if (store.isLoaded || loadingRef.current) return;

    const loadAllData = async () => {
      loadingRef.current = true;
      store.setLoading(true);

      try {
        console.log('[usePreloadOnboardingData] Starting data preload...');
        
        // Load all data in parallel
        const [shootingResult, planningResult, platformsResult, metricoolResult, userResult] = await Promise.all([
          getShootingPreferences(userId),
          getUserPlanningPreferences(userId),
          (async () => {
            await initializeUserPlatforms(userId);
            return getAllPlatforms(userId);
          })(),
          fetchMetricoolConnections(userId),
          getUserById(userId),
        ]);

        console.log('[usePreloadOnboardingData] Data loaded:', {
          shooting: shootingResult.success,
          planning: planningResult.success,
          platforms: platformsResult.success,
          metricool: metricoolResult.success,
          user: userResult.success,
        });

        // Store shooting preferences
        if (shootingResult.success) {
          useOnboardingDataStore.getState().setShootingPreferences(shootingResult.data || null);
        }

        // Store planning preferences
        if (planningResult.success) {
          useOnboardingDataStore.getState().setPlanningPreferences(planningResult.data);
        }

        // Store social platforms
        if (platformsResult.success && platformsResult.data) {
          useOnboardingDataStore.getState().setSocialPlatforms(platformsResult.data);
        }

        // Store Metricool connections
        if (metricoolResult.success && metricoolResult.connections) {
          const connectionsMap = new Map<string, MetricoolConnectionStatus>(
            metricoolResult.connections.map((c) => [c.platform.toLowerCase(), c] as [string, MetricoolConnectionStatus])
          );
          useOnboardingDataStore.getState().setMetricoolConnections(connectionsMap);
        }

        // Start prefetching the connection link in the background (non-blocking)
        // This makes the network connection step instant when user reaches it
        if (userResult.success && userResult.data?.metricool_blog_id) {
          const blogId = userResult.data.metricool_blog_id;
          const email = userResult.data.email ?? undefined;
          
          console.log('[usePreloadOnboardingData] Starting connection link prefetch...');
          useOnboardingDataStore.getState().setConnectionLinkLoading();
          
          // Fire and forget - don't block the onboarding flow
          getConnectionLink(userId, blogId, { email })
            .then((linkResult) => {
              if (linkResult.success && linkResult.link) {
                console.log('[usePreloadOnboardingData] Connection link prefetched successfully');
                useOnboardingDataStore.getState().setConnectionLinkSuccess(linkResult.link);
              } else {
                const errCode = linkResult.reason === 'token_expired' ? 'TOKEN_EXPIRED' : (linkResult.error || 'Erreur inconnue');
                console.warn('[usePreloadOnboardingData] Connection link prefetch failed:', errCode);
                useOnboardingDataStore.getState().setConnectionLinkError(errCode);
              }
            })
            .catch((err) => {
              console.error('[usePreloadOnboardingData] Connection link prefetch error:', err);
              const msg = err instanceof Error ? err.message : 'Erreur inconnue';
              const errCode = msg.toLowerCase().includes('connexions') ? 'TOKEN_EXPIRED' : msg;
              useOnboardingDataStore.getState().setConnectionLinkError(errCode);
            });
        } else {
          // No blog ID means we can't prefetch - mark as error so CTA shows fallback
          console.log('[usePreloadOnboardingData] No metricool_blog_id, skipping connection link prefetch');
        }

        useOnboardingDataStore.getState().setLoaded(true);
        console.log('[usePreloadOnboardingData] Preload complete');
      } catch (error) {
        console.error('[usePreloadOnboardingData] Error preloading data:', error);
        // Even on error, mark as loaded to prevent infinite retry
        useOnboardingDataStore.getState().setLoaded(true);
      } finally {
        useOnboardingDataStore.getState().setLoading(false);
        loadingRef.current = false;
      }
    };

    loadAllData();
  }, [userId]);

  return { isLoading, isLoaded, connectionLinkState };
}
